import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import factionService from '../../services/factionService';
import { getCharacterProfilePath } from '../../utils/characterPaths';
import styles from '../../pages/CharacterProfile/CharacterProfile.module.css';

const formatDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const normalizeSortOrder = (value, fallback = 0) => {
  if (value == null || !Number.isFinite(Number(value))) {
    return fallback;
  }
  const order = Number(value);
  if (order < 0 || order > 999999) {
    return fallback;
  }
  return order;
};

const buildSceneDraft = (threads = []) => {
  const draft = {};
  threads.forEach((thread, index) => {
    draft[thread.threadId] = {
      arcId: thread.arcId ?? '',
      sortOrder: normalizeSortOrder(thread.sortOrder, index),
      chronologyLabel: thread.chronologyLabel ?? '',
    };
  });
  return draft;
};

const buildArcDraft = (arcs = []) =>
  arcs.map((arc, index) => ({
    clientId: String(arc.id),
    id: arc.id,
    name: arc.name,
    sortOrder: arc.sortOrder ?? index,
  }));

const RpSceneCard = ({ thread }) => (
  <article className={styles.rpItem}>
    <Link to={`/threads/${thread.threadSlug}`} className={styles.rpItemMain}>
      <div className={styles.rpItemHeader}>
        {thread.chronologyLabel && (
          <span className={styles.rpChronology}>{thread.chronologyLabel}</span>
        )}
        <h3 className={styles.rpItemTitle}>{thread.title}</h3>
      </div>
      <div className={styles.rpItemMeta}>
        {thread.forum?.name && <span>{thread.forum.name}</span>}
        <span>{thread.replyCount} réponse{thread.replyCount !== 1 ? 's' : ''}</span>
        {thread.lastPostAt && (
          <span>Dernière activité : {formatDate(thread.lastPostAt)}</span>
        )}
      </div>
    </Link>
    {thread.participants?.length > 0 && (
      <div className={styles.rpParticipants}>
        <span className={styles.rpParticipantsLabel}>Avec</span>
        <div className={styles.rpParticipantsList}>
          {thread.participants.map((participant) => (
            <Link
              key={participant.id}
              to={getCharacterProfilePath(participant.id)}
              className={styles.rpParticipant}
            >
              {participant.avatar ? (
                <img src={participant.avatar} alt="" className={styles.rpParticipantAvatar} />
              ) : (
                <span className={styles.rpParticipantFallback}>
                  {(participant.name || '?').charAt(0).toUpperCase()}
                </span>
              )}
              <span>{participant.name}</span>
            </Link>
          ))}
        </div>
      </div>
    )}
  </article>
);

const FactionRpScenesPanel = ({
  factionId,
  rpArcs = [],
  rpThreads = [],
  canOrganize = false,
  onOrganizationSaved,
}) => {
  const [organizing, setOrganizing] = useState(false);
  const [draftArcs, setDraftArcs] = useState([]);
  const [draftScenes, setDraftScenes] = useState({});
  const [newArcName, setNewArcName] = useState('');
  const [savingOrganization, setSavingOrganization] = useState(false);
  const [organizationMessage, setOrganizationMessage] = useState('');

  const groupedRpScenes = useMemo(() => {
    const groups = rpArcs.map((arc) => ({
      arc,
      threads: rpThreads.filter((thread) => thread.arcId === arc.id),
    }));
    const unassigned = rpThreads.filter((thread) => !thread.arcId);
    if (unassigned.length > 0) {
      groups.push({ arc: null, threads: unassigned });
    }
    return groups;
  }, [rpArcs, rpThreads]);

  const startOrganizing = () => {
    setDraftArcs(buildArcDraft(rpArcs));
    setDraftScenes(buildSceneDraft(rpThreads));
    setNewArcName('');
    setOrganizationMessage('');
    setOrganizing(true);
  };

  const cancelOrganizing = () => {
    setOrganizing(false);
    setOrganizationMessage('');
  };

  const addArc = () => {
    const name = newArcName.trim();
    if (!name) return;
    setDraftArcs((prev) => [
      ...prev,
      {
        clientId: `new-${Date.now()}-${prev.length}`,
        name,
        sortOrder: prev.length,
      },
    ]);
    setNewArcName('');
  };

  const removeArc = (clientId) => {
    setDraftArcs((prev) => prev.filter((arc) => arc.clientId !== clientId));
    setDraftScenes((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((threadId) => {
        if (String(next[threadId].arcId) === String(clientId)) {
          next[threadId] = { ...next[threadId], arcId: '' };
        }
      });
      return next;
    });
  };

  const updateSceneDraft = (threadId, field, value) => {
    setDraftScenes((prev) => ({
      ...prev,
      [threadId]: {
        ...prev[threadId],
        [field]: value,
      },
    }));
  };

  const saveOrganization = async () => {
    setSavingOrganization(true);
    setOrganizationMessage('');
    try {
      const payload = {
        arcs: draftArcs.map((arc, index) => ({
          clientId: arc.clientId,
          id: arc.id,
          name: arc.name,
          sortOrder: index,
        })),
        scenes: rpThreads.map((thread, index) => ({
          threadId: thread.threadId,
          arcId: draftScenes[thread.threadId]?.arcId || null,
          sortOrder: normalizeSortOrder(draftScenes[thread.threadId]?.sortOrder, index),
          chronologyLabel: draftScenes[thread.threadId]?.chronologyLabel || '',
        })),
      };
      const result = await factionService.updateRpOrganization(factionId, payload);
      onOrganizationSaved?.(result);
      setOrganizing(false);
      setOrganizationMessage('Organisation enregistrée.');
    } catch (err) {
      setOrganizationMessage(err.response?.data?.error || 'Erreur lors de la sauvegarde.');
    } finally {
      setSavingOrganization(false);
    }
  };

  useEffect(() => {
    if (!organizing) {
      setOrganizationMessage('');
    }
  }, [rpArcs, rpThreads, organizing]);

  if (!rpThreads.length) {
    return <p className={styles.emptyState}>Aucune scène RP liée à cette faction.</p>;
  }

  return (
    <div>
      {canOrganize && !organizing && (
        <div className={styles.rpToolbar}>
          <button type="button" className={styles.organizeButton} onClick={startOrganizing}>
            Organiser les scènes
          </button>
        </div>
      )}

      {organizing && (
        <div className={styles.organizePanel}>
          <h3 className={styles.organizeTitle}>Organisation des scènes RP de la faction</h3>
          <p className={styles.organizeHint}>
            Crée des arcs narratifs, assigne les scènes et définis une chronologie pour la faction.
          </p>

          <div className={styles.arcEditor}>
            <h4 className={styles.arcEditorTitle}>Arcs</h4>
            <div className={styles.addArcRow}>
              <input
                type="text"
                className={styles.organizeInput}
                value={newArcName}
                onChange={(event) => setNewArcName(event.target.value)}
                placeholder="Nom de l’arc"
              />
              <button type="button" className={styles.organizeAction} onClick={addArc}>
                Ajouter
              </button>
            </div>
            {draftArcs.length > 0 ? (
              <ul className={styles.arcList}>
                {draftArcs.map((arc) => (
                  <li key={arc.clientId} className={styles.arcListItem}>
                    <input
                      type="text"
                      className={styles.organizeInput}
                      value={arc.name}
                      onChange={(event) => {
                        setDraftArcs((prev) => prev.map((item) => (
                          item.clientId === arc.clientId
                            ? { ...item, name: event.target.value }
                            : item
                        )));
                      }}
                    />
                    <button
                      type="button"
                      className={styles.organizeActionMuted}
                      onClick={() => removeArc(arc.clientId)}
                    >
                      Retirer
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.organizeEmpty}>Aucun arc défini pour le moment.</p>
            )}
          </div>

          <div className={styles.sceneEditor}>
            <h4 className={styles.arcEditorTitle}>Scènes</h4>
            {rpThreads.map((thread) => (
              <div key={thread.threadId} className={styles.sceneEditorRow}>
                <div className={styles.sceneEditorTitle}>{thread.title}</div>
                <input
                  type="text"
                  className={styles.organizeInput}
                  value={draftScenes[thread.threadId]?.chronologyLabel ?? ''}
                  onChange={(event) => updateSceneDraft(thread.threadId, 'chronologyLabel', event.target.value)}
                  placeholder="Chronologie (ex. 003 — Printemps)"
                />
                <select
                  className={styles.organizeSelect}
                  value={draftScenes[thread.threadId]?.arcId ?? ''}
                  onChange={(event) => updateSceneDraft(thread.threadId, 'arcId', event.target.value)}
                >
                  <option value="">Sans arc</option>
                  {draftArcs.map((arc) => (
                    <option key={arc.clientId} value={arc.clientId}>
                      {arc.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className={styles.organizeActions}>
            <button
              type="button"
              className={styles.organizeAction}
              onClick={saveOrganization}
              disabled={savingOrganization}
            >
              {savingOrganization ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              type="button"
              className={styles.organizeActionMuted}
              onClick={cancelOrganizing}
              disabled={savingOrganization}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {organizationMessage && (
        <p className={styles.organizationMessage}>{organizationMessage}</p>
      )}

      <div className={styles.rpGroups}>
        {groupedRpScenes.map((group) => (
          <section key={group.arc?.id ?? 'unassigned'} className={styles.rpGroup}>
            <h4 className={styles.rpGroupTitle}>
              {group.arc ? group.arc.name : 'Autres scènes'}
            </h4>
            <div className={styles.rpList}>
              {group.threads.map((thread) => (
                <RpSceneCard key={thread.threadId} thread={thread} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default FactionRpScenesPanel;
